<template>
  <div>
    <div class="activity-stage dolch-space">
      <div v-if="currentRound.steps" class="typing-stage">
        <div
          v-for="(step, index) in currentRound.steps"
          :key="index + step.answer"
          :class="`td-container step-${index}`"
        >
          <color-block-text class="type-line" :text="step.answer" :text-step="index">
          </color-block-text>
        </div>
      </div>
      <div v-if="currentRound.steps" class="image-stage">
        <img class="image-before" :src="currentRound.imageBefore" />
        <img class="image-after" :src="currentRound.image" />
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';

import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  name: 'DolchSpace',

  components: {
    ColorBlockText
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager],

  data() {
    return {
      sfx: {
        soundsPerRounds: null
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    shouldEndRound() {
      const { currentIndex } = this.typingProgress;
      return this.isLastStepInRound && currentIndex === 0;
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
      const [sounds] = await this.loadAssets(this.loadSounds, this.loadImages);
      this.sfx = sounds;
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          written: 'Type each phrase.',
          voice: `${this.voiceInstructionsPrefix}/dolchandspacenth-fa-1.mp3`,
          delay: 2
        }
      };
    },

    async loadImages() {
      this.rounds.forEach(({ imageBefore, image }) => this.queueImage(imageBefore, image));

      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds.map(({ audio }) => audio);

      this.queueAudio(soundPaths);

      const assetCache = await this.downloadAudio();

      return {
        soundsPerRound: soundPaths.map(path => assetCache.get(path))
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startRound() {
      this.hideColorImage();
      this.setupNewRound();
      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, {
          autoAlpha: 1,
          onComplete: this.enableKeyboard
        });
      });
    },

    showColorImage() {
      TweenMax.to('.image-after', 0.5, { autoAlpha: 1 });
    },

    hideColorImage() {
      TweenMax.set('.image-after', { autoAlpha: 0 });
    },

    handleCorrectKey() {
      if (this.shouldEndRound) {
        this.finishRound();
        return;
      }

      this.sndType();
      this.nextCharacter();
    },

    nextCharacter() {
      this.incrementTypingIndex();

      if (this.isEndOfCurrentWord) {
        this.setCurrentStep();
      }
    },

    sayPhrase() {
      const audio = this.sfx.soundsPerRound[this.progress.roundIndex];

      if (audio && audio.play) {
        audio.play();
      }
    },

    finishRound() {
      const handleNextAction = this.isLastRound ? this.completeActivity : this.startRound;

      this.disableKeyboard();
      this.sndBell();
      this.sayPhrase();
      this.showColorImage();

      TweenMax.to('.activity-stage', 0.5, {
        delay: 1,
        autoAlpha: 0,
        onComplete: handleNextAction
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-stage.dolch-space {
  width: 100%;
  display: flex;

  .typing-stage {
    display: flex;
    flex: 1;
    justify-content: center;
    width: 54%;
    text-align: center;
    vertical-align: top;
    margin-top: 20%;

    p.tdk2 {
      color: #888888;
    }

    .td-container {
      display: inline-block;
      min-width: 30px;

      .type-line {
        position: relative;
        font-size: 50px;
        font-family: hwtprint;
        height: 160px;

        span {
          display: inline-block;
        }

        .keyStyle {
          padding: 15px 50px 15px 50px;
        }

        &.spacebar {
          background-position: center center;
          margin-right: 130px;
        }
      }
    }
  }

  .image-stage {
    display: inline-block;
    width: 45%;
    position: relative;
    text-align: left;

    img {
      height: 100%;
      max-height: 450px;
      max-width: 450px;
    }

    .image-after {
      position: absolute;
      visibility: hidden;
      top: 0;
      left: 0;
    }
  }

  @media screen and (max-height: 800px) {
    .typing-stage {
      margin-top: 10%;
    }
    img {
      max-height: 400px;
    }
  }
}
</style>
